import React from 'react';
import { Box, FileUploader, IconButton, Text } from '@nimbus-ds/components';
import { TrashIcon } from '@nimbus-ds/icons';
import { ThumbnailWithAction } from '@nimbus-ds/patterns';
import { useDocumentUploader } from 'commons/hooks/useDocumentUploader';
import { DocumentUploaderProps } from 'commons/types';
import { config } from 'config';
import DocumentUploaderSkeleton from './DocumentUploaderSkeleton';

function DocumentUploader({
  ariaLabel,
  aspectRatio,
  documentName,
  helperText,
  onSuccess,
  onError,
  onDelete,
  onFileSelect,
  showPreview,
  autoUpload,
  initialThumbnailFile,
  fullWidth,
  isMobile,
  disabled,
  accept,
  maxDocumentSize = config.documents.maxSize,
  errorEventName,
}: DocumentUploaderProps): JSX.Element {
  const {
    thumbnailImageSrc,
    isLoading,
    preview,
    fileName,
    handleFileChange,
    handleFileDelete,
  } = useDocumentUploader({
    documentName,
    onSuccess,
    onError,
    onDelete,
    onFileSelect,
    showPreview,
    autoUpload,
    initialThumbnailFile,
    maxDocumentSize,
    errorEventName,
  });

  if (isLoading) {
    return <DocumentUploaderSkeleton aspectRatio={aspectRatio} />;
  }

  if (thumbnailImageSrc || preview) {
    return (
      <Box width={fullWidth && !isMobile ? '350px' : '100%'}>
        <ThumbnailWithAction
          thumbnail={{
            src: thumbnailImageSrc || preview || '',
            alt: ariaLabel,
            aspectRatio: aspectRatio,
            width: '100%',
            height: '180px',
            className: 'document-uploader-thumbnail-img',
          }}
        >
          <IconButton
            source={<TrashIcon color="black" />}
            size="2rem"
            onClick={handleFileDelete}
          />
        </ThumbnailWithAction>
      </Box>
    );
  }

  if (fileName && (fileName.endsWith('.pdf') || fileName.endsWith('.PDF'))) {
    return (
      <Box
        borderStyle="dashed"
        borderColor="primary-interactive"
        borderWidth="1"
        borderRadius="2"
        backgroundColor="neutral-surface"
        width={fullWidth && !isMobile ? '350px' : '100%'}
        height="180px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="primary-interactive" fontWeight="bold">
          {fileName}
        </Text>
        <IconButton
          source={<TrashIcon color="black" />}
          size="2rem"
          onClick={handleFileDelete}
        />
      </Box>
    );
  }

  return (
    <Box width={fullWidth && !isMobile ? '350px' : '100%'}>
      <FileUploader
        aspectRatio={aspectRatio}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFileChange(file);
        }}
        placeholder={helperText}
        disabled={disabled}
        accept={accept}
        id={documentName}
        data-testid={`file-input-${documentName}`}
      />
    </Box>
  );
}

export default DocumentUploader;
